import Grid from '@/roc/grid/Grid';
import Eras from '@/roc/era';
import BuildingType from '@/roc/building/BuildingType';
import EraType from "@/roc/era/EraType";
import { BuildingFlags } from "@/roc/building/BuildingTemplate";
import { useApplicationStore } from "@/roc/stores/ApplicationStore";
import Rect from "@/roc/grid/Rect";
import { useLayoutStore } from "@/roc/stores/LayoutStore";
import AbstractProduction from "@/roc/effects/Production/AbstractProduction";
import HappinessMod from "@/roc/effects/Production/HappinessMod";
import Effects from "@/roc/effects/Effects";
import { useWonderStore } from "@/roc/stores/WonderStore";
import PassiveAura from "@/roc/effects/PassiveAura/PassiveAura";
import PassiveAuraType from "@/roc/effects/PassiveAura/PassiveAuraType";
import { applyWonderSynergies } from "@/components/Bonuses/Calculator/Bonus";
import SynergyMod from "@/roc/wonders/base/SynergyMod";
import BuildingLevel from "@/roc/building/BuildingLevel";
import BuildingEffectAmount from "@/roc/building/BuildingEffectAmount";
class BuildingInstance {
    __template;
    __x;
    __y;
    __isRotated;
    __level;
    __era;
    __id;
    __happiness;
    __effects;
    constructor(template, x, y, isRotated, level, era, id) {
        this.__template = template;
        this.__x = x;
        this.__y = y;
        this.__isRotated = isRotated;
        this.__level = level;
        this.__era = era;
        this.__id = id;
        this.__happiness = null;
        const cloned = [];
        for (let i = 0; i < this.__template.effects.count(); i++) {
            cloned.push(this.__template.effects.getEffect(i).clone());
        }
        this.__effects = new Effects(cloned);
    }
    get id() {
        return this.__id;
    }
    get template() {
        return this.__template;
    }
    get templateId() {
        return this.__template.id;
    }
    get width() {
        return this.__template.width;
    }
    get height() {
        return this.__template.height;
    }
    get x() {
        return this.__x;
    }
    get y() {
        return this.__y;
    }
    get level() {
        return this.__level;
    }
    get era() {
        return this.__era;
    }
    get minEra() {
        return this.__template.era;
    }
    get placementInfo() {
        return {
            x: this.__x * Grid.CELL_SIZE,
            y: this.__y * Grid.CELL_SIZE,
            rotate: this.__isRotated,
            level: this.__level,
            era: this.__era,
        };
    }
    get isRotated() {
        return this.__isRotated;
    }
    get name() {
        return this.__template.name;
    }
    get type() {
        return this.__template.type;
    }
    get isLuxurious() {
        return (this.__template.flags & BuildingFlags.LUXURIOUS) !== 0;
    }
    get minLevel() {
        return this.__template.minLevel;
    }
    get isEvent() {
        return BuildingType.EVENT === this.type;
    }
    get isCityHall() {
        return BuildingType.CITY_HALL === this.type;
    }
    get isCollectable() {
        return BuildingType.COLLECTABLE === this.type;
    }
    get hasHappiness() {
        return this.__effects.getHappiness() !== null && this.happiness !== null;
    }
    get hasCultureBonus() {
        return this.__effects.providesCulture();
    }
    get effects() {
        return this.__effects;
    }
    get happiness() {
        return this.__happiness;
    }
    serialize() {
        return [
            this.templateId,
            this.x,
            this.y,
            this.isRotated ? 1 : 0,
            this.level,
            this.era ? Eras[this.era].id : '0',
            0
        ].join('.');
    }
    getCulture() {
        const appStore = useApplicationStore();
        if (this.isEvent || this.isCollectable) {
            return this.effects.getCulture(this.level, this.era);
        }
        return this.effects.getCulture(this.level, appStore.selectedEra || EraType.stoneAge);
    }
    calculateHappiness() {
        const appStore = useApplicationStore();
        const lytStore = useLayoutStore();
        const happiness = this.__effects.getHappiness();
        if (!happiness) {
            return;
        }
        const era = BuildingLevel.calculateBuildingEraFromLevel(this.level, this.__template, appStore.selectedEra || EraType.stoneAge);
        if (!happiness.hasAmountForEra(era)) {
            return;
        }
        const refBuilding = document.querySelector('[data-building-reference="' + this.id + '"]');
        if (!(refBuilding instanceof HTMLElement)) {
            return;
        }
        this.__happiness = {
            value: 0,
            level: 0,
            required: 0,
        };
        const myRect = new Rect(parseInt(refBuilding.style.left.replace('px', '')), parseInt(refBuilding.style.top.replace('px', '')), refBuilding.clientWidth - 1, refBuilding.clientHeight - 1);
        const testBuilding = (cbBuilding, culture) => {
            const bonusRect = new Rect(parseInt(cbBuilding.style.left.replace('px', '')) - culture.range * Grid.CELL_SIZE, parseInt(cbBuilding.style.top.replace('px', '')) - culture.range * Grid.CELL_SIZE, cbBuilding.clientWidth - 1 + 2 * culture.range * Grid.CELL_SIZE, cbBuilding.clientHeight - 1 + 2 * culture.range * Grid.CELL_SIZE);
            if (myRect.intersects(bonusRect)) {
                // @ts-ignore: we can ignore this because we initialized it properly above
                this.__happiness.value += culture.value;
            }
        };
        // Apply culture from placed buildings
        lytStore.mapBuildings((building, buildingId) => {
            if (building.hasCultureBonus) {
                const culture = building.getCulture();
                const cbBuilding = document.querySelector('[data-building-reference="' + buildingId + '"]');
                if (cbBuilding instanceof HTMLElement && culture) {
                    testBuilding(cbBuilding, culture);
                }
            }
            return true;
        });
        // Apply culture from pending buildings
        const pBuildings = document.getElementsByClassName('pending-building');
        if (pBuildings.length > 0 && pBuildings[0] instanceof HTMLElement) {
            const pBuilding = pBuildings[0];
            if (pBuilding.classList.contains('culture-bonus')) {
                const culture = {
                    range: parseInt(pBuilding.dataset.buildingCultureBonusRange || '0'),
                    value: parseInt(pBuilding.dataset.buildingCultureBonus || '0'),
                };
                if (culture.range > 0 && culture.value > 0) {
                    testBuilding(pBuilding, culture);
                }
            }
        }
        // Calculate happiness level
        if (this.__happiness.value > 0) {
            for (let i = 2; i >= 0; i--) {
                if (happiness.getRequired(era, i) > 0 && this.__happiness.value >= happiness.getRequired(era, i)) {
                    this.__happiness.level = i + 1;
                    break;
                }
            }
        }
        // Calculate required happiness
        if (this.__happiness.level === 3) {
            this.__happiness.required = happiness.getRequired(era, 2);
        }
        else {
            for (let i = 0; i < 3; i++) {
                if (this.__happiness.value < happiness.getRequired(era, i)) {
                    this.__happiness.required = happiness.getRequired(era, i);
                    break;
                }
            }
        }
        if (this.__happiness.level === 0) {
            return;
        }
        // Apply mod to productions
        for (let i = 0; i < this.__effects.count(); i++) {
            const effect = this.__effects.getEffect(i);
            if (effect instanceof AbstractProduction) {
                const amount = effect.output.calculate(this.level, era);
                let multiplier = 0;
                switch (this.__happiness.level) {
                    case 1:
                        multiplier = 0.25;
                        break;
                    case 2:
                        multiplier = 0.5;
                        break;
                    case 3:
                        multiplier = 1;
                        break;
                }
                effect.removeModOfType(HappinessMod, i);
                effect.withMod(new HappinessMod(amount * multiplier, i));
            }
        }
    }
    applyProductionModifiers() {
        const outputs = {
            gold: false,
            food: false,
            goods: false,
        };
        if (this.effects.count() > 0) {
            for (let i = 0; i < this.effects.count(); i++) {
                const eff = this.effects.getEffect(i);
                if (!(eff instanceof AbstractProduction)) {
                    continue;
                }
                if (eff.producesGoods()) {
                    outputs.goods = true;
                }
                else if (eff.producesFood()) {
                    outputs.food = true;
                }
                else if (eff.producesGold()) {
                    outputs.gold = true;
                }
            }
        }
        if (!outputs.gold && !outputs.food && !outputs.goods) {
            return;
        }
        const wonderStore = useWonderStore();
        const appStore = useApplicationStore();
        const lytStore = useLayoutStore();
        const effectAmount = {
            gold: new BuildingEffectAmount(),
            food: new BuildingEffectAmount(),
            goods: new BuildingEffectAmount(),
        };
        wonderStore.mapWonders((wonder, config) => {
            if (config.active) {
                for (let i = 0; i < wonder.effects.count(); i++) {
                    const effect = wonder.effects.getEffect(i);
                    if (effect instanceof PassiveAura) {
                        if (effect.type === PassiveAuraType.GOLD_PRODUCTION_MOD && outputs.gold) {
                            const am = effect.getAmount(config.level, EraType.stoneAge);
                            if (am) {
                                effectAmount.gold.increase(am);
                                applyWonderSynergies(wonder, config.level, i, (effect, amount) => {
                                    effectAmount.gold.increase(amount);
                                });
                            }
                        }
                        else if (effect.type === PassiveAuraType.FOOD_PRODUCTION_MOD && outputs.food) {
                            const am = effect.getAmount(config.level, EraType.stoneAge);
                            if (am) {
                                effectAmount.food.increase(am);
                                applyWonderSynergies(wonder, config.level, i, (effect, amount) => {
                                    effectAmount.food.increase(amount);
                                });
                            }
                        }
                        else if (effect.type === PassiveAuraType.CAPITAL_CITY_GOODS_PRODUCTION_MOD && outputs.goods) {
                            const am = effect.getAmount(config.level, EraType.stoneAge);
                            if (am) {
                                effectAmount.goods.increase(am);
                                applyWonderSynergies(wonder, config.level, i, (effect, amount) => {
                                    // @ts-ignore
                                    effectAmount.goods.increase(amount);
                                });
                            }
                        }
                    }
                }
            }
            return true;
        });
        lytStore.mapBuildings((building, buildingId) => {
            if (building.id !== this.id) {
                for (let i = 0; i < building.effects.count(); i++) {
                    const effect = building.effects.getEffect(i);
                    if (effect instanceof PassiveAura) {
                        switch (effect.type) {
                            case PassiveAuraType.GOLD_PRODUCTION_MOD:
                                if (outputs.gold) {
                                    effectAmount.gold.increase(effect.getAmount(building.level, appStore.selectedEra || EraType.stoneAge));
                                }
                                break;
                            case PassiveAuraType.FOOD_PRODUCTION_MOD:
                                if (outputs.food) {
                                    effectAmount.food.increase(effect.getAmount(building.level, appStore.selectedEra || EraType.stoneAge));
                                }
                                break;
                            case PassiveAuraType.CAPITAL_CITY_GOODS_PRODUCTION_MOD:
                                if (outputs.goods) {
                                    effectAmount.goods.increase(effect.getAmount(building.level, appStore.selectedEra || EraType.stoneAge));
                                }
                                break;
                        }
                    }
                }
            }
            return true;
        });
        for (let i = 0; i < this.effects.count(); i++) {
            const effect = this.effects.getEffect(i);
            const baseEffect = this.__template.effects.getEffect(i);
            if (effect instanceof AbstractProduction && baseEffect instanceof AbstractProduction) {
                const amount = baseEffect.output.calculate(this.level, appStore.selectedEra || EraType.stoneAge);
                if (effect.producesGold()) {
                    effect.removeModOfType(SynergyMod, i);
                    if (effectAmount.gold.canAffect()) {
                        effect.withMod(new SynergyMod(effectAmount.gold.applyTo(amount), i));
                    }
                }
                else if (effect.producesFood()) {
                    effect.removeModOfType(SynergyMod, i);
                    if (effectAmount.food.canAffect()) {
                        effect.withMod(new SynergyMod(effectAmount.food.applyTo(amount), i));
                    }
                }
                else if (effect.producesGoods()) {
                    effect.removeModOfType(SynergyMod, i);
                    if (effectAmount.goods.canAffect()) {
                        effect.withMod(new SynergyMod(effectAmount.goods.applyTo(amount), i));
                    }
                }
            }
        }
    }
}
export default BuildingInstance;
